import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Card, ExitPage, Field, Modal, Pill, ReadField, Stepper } from "./ExitParts";
import { dateFromToday, exitApi, fmtDate, fmtTs } from "./exitApi";

// Fields the employee may edit on their own (EMP) stage; the rest of the form is read-only for them.
// HR may also edit these (and pick the RM) in Employee Details once the exit reaches the HR stage.
const EMP_EDITABLE = ["personalEmail", "nameAsPerBank", "bankName", "bankAccountNo", "branchName", "ifsc"];

// Standard Indian bank formats: account number 9-18 digits; IFSC = 4 letters, "0", 6 letters/digits.
const ACCOUNT_NO_RE = /^\d{9,18}$/;
const IFSC_RE = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/;

const NOT_AUTHORIZED_MSG = "You are not authorized to modify this information. Please contact HR for any changes.";
const FF_DATE_MSG = "The F&F Trigger Date can be backdated up to 90 days from the current date. Future dates are not allowed.";
const LWD_MSG = "The Date of Leaving must be within 90 days before or after today.";
const LWD_FIELD = { key: "date_leaving", label: "Date of Leaving (LWD)", type: "date", required: true };

const isVisible = (f, values) => !f.showIf || values[f.showIf[0]] === f.showIf[1];

// Admin: "Infra submitted to IT for Data Backup" is optional once any asset is "Not Issued".
const ADMIN_ASSETS = ["laptop", "mobile", "dataCard", "penDrive"];
const withDynamicRequired = (f, values) =>
  f.key === "infraToIT" && ADMIN_ASSETS.some((k) => values[k] === "Not Issued") ? { ...f, required: false } : f;

const bankFormatErrors = (values) => {
  const e = {};
  if (values.bankAccountNo && !ACCOUNT_NO_RE.test(String(values.bankAccountNo).trim()))
    e.bankAccountNo = "Enter a valid bank account number (9 to 18 digits).";
  if (values.ifsc && !IFSC_RE.test(String(values.ifsc).trim()))
    e.ifsc = "Enter a valid IFSC code (e.g. SBIN0001234).";
  return e;
};

// `rmOptions` is passed to the EMP stage for the case's HR approver, at any point in the case: HR edits
// EMP_EDITABLE and the RM in Employee Details and saves them with that section's own Save button.
function StageSection({ exitCase, stage, onChanged, rmOptions }) {
  const form = exitCase.form[stage.stage_key] || { fields: [] };
  const canAct = exitCase.can_act_now.includes(stage.stage_key);
  const isOpen = stage.status === "open" && exitCase.status === "open";
  const hrEditing = !!rmOptions && stage.stage_key === "EMP";
  const [collapsed, setCollapsed] = useState(!canAct && !hrEditing);
  // A field's "default" pre-selects it for exits started before the field existed (e.g. Eligible for Rehire).
  const [vals, setVals] = useState(() => Object.fromEntries(form.fields.map((f) => [f.key, exitCase.values[f.key] ?? f.default ?? ""])));
  const [rmId, setRmId] = useState(exitCase.rm?.id || "");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  // HR/admin (who hold other stages) keep full edit access; the employee alone is restricted.
  const employeeOnly = stage.stage_key === "EMP" && exitCase.my_stages.every((k) => k === "EMP");
  const isLocked = (f) => employeeOnly && !f.declaration && !EMP_EDITABLE.includes(f.key);

  const set = (k, v) => {
    setVals((x) => ({ ...x, [k]: v }));
    // Picking "Not Issued" for an asset makes infraToIT optional, so drop its stale "Required" error.
    setErrors((e) => ({ ...e, [k]: undefined, ...(ADMIN_ASSETS.includes(k) && v === "Not Issued" ? { infraToIT: undefined } : {}) }));
  };
  const merged = { ...exitCase.values, ...vals };

  // The case's current RM is always selectable, even if it isn't in the Denave RM list.
  const rmChoices = !rmOptions ? [] : exitCase.rm && !rmOptions.some((u) => u.id === exitCase.rm.id) ? [exitCase.rm, ...rmOptions] : rmOptions;

  // Always live from exitCase.computed, even once the stage is done: these three fields (applicableNotice,
  // actualNoticeServed, noticeShortfall) must keep reflecting the current LWD, not a frozen submit-time snapshot.
  const computedValue = (f) => {
    const c = exitCase.computed;
    if (f.computed !== "noticeShortfall") return c[f.computed];
    // Recalculate live while the RM/HR is editing the waiver.
    const waived = merged.shortfallAction === "To be Waived Off" ? Number(merged.waivedDays) || 0 : 0;
    return Math.max(0, (c.applicableNotice || 0) - (c.actualNoticeServed || 0) - waived);
  };

  const act = async (kind) => {
    // F&F Trigger Date: today-90 .. today (backstop for typed dates; the calendar is limited via min/max).
    if (vals.ffDate && (vals.ffDate < dateFromToday(-90) || vals.ffDate > dateFromToday(0))) {
      setErrors((e) => ({ ...e, ffDate: FF_DATE_MSG }));
      return;
    }
    // Bank formats are checked on submit only, so a half-filled draft can still be saved.
    const bankErrors = kind === "draft" ? {} : bankFormatErrors(vals);
    if (Object.keys(bankErrors).length) {
      setErrors((e) => ({ ...e, ...bankErrors }));
      return;
    }
    setBusy(true);
    try {
      const res = kind === "draft"
        ? await exitApi.saveDraft(exitCase.id, stage.stage_key, vals)
        : await exitApi.submitStage(exitCase.id, stage.stage_key, vals);
      toast.success(res.message);
      onChanged(res.data);
    } catch (e) {
      if (e.details && Object.keys(e.details).length) setErrors(e.details);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  // HR's Save in Employee Details: the six bank/email fields and the RM.
  const saveDetails = async () => {
    const values = Object.fromEntries(EMP_EDITABLE.map((k) => [k, vals[k]]));
    const bankErrors = bankFormatErrors(values);
    if (Object.keys(bankErrors).length) {
      setErrors((e) => ({ ...e, ...bankErrors }));
      return;
    }
    setBusy(true);
    try {
      const res = await exitApi.saveEmployeeDetails(exitCase.id, { values, rm_id: rmId || null });
      toast.success(res.message);
      onChanged(res.data);
    } catch (e) {
      if (e.details && Object.keys(e.details).length) setErrors((x) => ({ ...x, ...e.details }));
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const status = stage.status === "done" ? <Pill kind="done">Cleared by {stage.done_by} · {fmtTs(stage.done_at)}</Pill>
    : stage.status === "skipped" ? <Pill>Skipped</Pill>
    : isOpen ? <Pill kind={stage.sla?.state}>Pending with {stage.label}</Pill>
    : <Pill>Not started</Pill>;

  return (
    <Card
      className={canAct || hrEditing ? "editable" : ""}
      title={form.label === "Employee" ? "Employee Details" : `Clearance from ${form.label || stage.label}`}
      right={<>{status}<span className="spacer" /><span className={`chev ${collapsed ? "" : "up"}`} aria-hidden="true">⌃</span></>}
      onTitleClick={() => setCollapsed(!collapsed)}
    >
      {!collapsed && (
        <>
          {stage.stage_key === "EMP" && (
            <div className="exitwf-grid">
              <ReadField label="Employee Name" value={exitCase.employee_snapshot.name} />
              <ReadField label="Employee Code" value={exitCase.employee_snapshot.employee_code} />
              <ReadField label="Date of Joining" value={fmtDate(exitCase.employee_snapshot.date_of_joining)} />
              <ReadField label="Department" value={exitCase.employee_snapshot.department} />
              <ReadField label="Designation" value={exitCase.employee_snapshot.designation} />
              <ReadField label="Project" value={exitCase.employee_snapshot.project} />
              <ReadField label="Mobile" value={exitCase.employee_snapshot.mobile} />
              <ReadField label="Official Email Id" value={exitCase.employee_snapshot.email} />
              {hrEditing ? (
                <div className={`exitwf-field ${errors.rm_id ? "has-error" : ""}`}>
                  <label htmlFor="exf-rm">Reporting Manager</label>
                  <select id="exf-rm" className="form-select" value={rmId} disabled={busy}
                    onChange={(e) => { setRmId(e.target.value); setErrors((x) => ({ ...x, rm_id: undefined })); }}>
                    {!rmId && <option value="">Select</option>}
                    {rmChoices.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                  {errors.rm_id && <div className="err">{errors.rm_id}</div>}
                </div>
              ) : (
                <ReadField label="Reporting Manager" value={exitCase.employee_snapshot.rm_name} />
              )}
            </div>
          )}
          {hrEditing && <p className="exitwf-hint">HR can update the Reporting Manager and the email and bank details here.</p>}
          {employeeOnly && (
            <p className="exitwf-hint">
              You can update only your personal email and bank details. For the Reporting Manager and the other fields: {NOT_AUTHORIZED_MSG}
            </p>
          )}
          <div className="exitwf-grid">
            {form.fields.filter((f) => isVisible(f, merged)).map((f) => withDynamicRequired(f, merged)).map((f) =>
              f.computed ? (
                <ReadField key={f.key} label={f.label} value={computedValue(f)} />
              ) : (
                <Field key={f.key} field={f} value={vals[f.key]} onChange={set} error={errors[f.key]} options={exitCase.options}
                  readOnly={busy || (canAct ? isLocked(f) : hrEditing ? !EMP_EDITABLE.includes(f.key) : true)}
                  min={f.key === "ffDate" ? dateFromToday(-90) : undefined} max={f.key === "ffDate" ? dateFromToday(0) : undefined} />
              )
            )}
          </div>
          {(canAct || hrEditing) && (
            <div className="exitwf-actions">
              {canAct && <button className="exitwf-btn ghost" disabled={busy} onClick={() => act("draft")}>Save Draft</button>}
              {/* The stage actor's Save still submits and progresses the workflow; HR's standing edit
                  access (outside their own turn) only patches the employee-details fields via saveDetails. */}
              <button className="exitwf-btn" disabled={busy} onClick={canAct ? () => act("submit") : saveDetails}>Save</button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export default function ExitCaseView() {
  const { id } = useParams();
  const [exitCase, setExitCase] = useState(null);
  const [error, setError] = useState("");
  const [cancel, setCancel] = useState(null);
  const [lwdValue, setLwdValue] = useState("");
  const [lwdError, setLwdError] = useState();
  const [lwdBusy, setLwdBusy] = useState(false);
  const [rmOptions, setRmOptions] = useState([]); // Denave RMs for HR's dropdown in Employee Details

  // Reset the LWD editor to the saved date whenever the case is (re)loaded.
  useEffect(() => {
    setLwdValue(exitCase?.separation.date_leaving || "");
    setLwdError(undefined);
  }, [exitCase]);

  // HR may edit the LWD only once the exit reaches the HR stage; HR may edit the employee's bank/email
  // details and the RM at any point in the case, even before the case reaches HR's turn.
  const hrActive = !!exitCase?.can_act_now.includes("HR");
  const isHrApprover = exitCase?.status === "open" && !!exitCase?.my_stages.includes("HR");
  useEffect(() => {
    if (isHrApprover) exitApi.denaveRms().then((r) => setRmOptions(r.data || [])).catch(() => {});
  }, [isHrApprover]);

  const load = useCallback(async () => {
    try {
      setExitCase((await exitApi.getCase(id)).data);
    } catch (e) {
      setError(e.code === 404 ? "This exit doesn't exist, or you don't have access to it." : e.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <ExitPage title="Exit Clearance">
        <Card title="Exit not available"><p>{error}</p><Link to="/exit-clearance">Back to Exit Clearance</Link></Card>
      </ExitPage>
    );
  }
  if (!exitCase) return <ExitPage title="Exit Clearance"><Card><p className="exitwf-hint">Loading…</p></Card></ExitPage>;

  const pendingLabels = exitCase.stages_detail.filter((s) => exitCase.pending_at.includes(s.stage_key)).map((s) => s.label);
  const sep = exitCase.separation;
  const canCancel = isHrApprover;
  const canEditLwd = hrActive;

  // HR's Save in Separation Details: the LWD may be moved to today-90 .. today+90.
  const saveLwd = async () => {
    if (!lwdValue || lwdValue < dateFromToday(-90) || lwdValue > dateFromToday(90)) {
      setLwdError(LWD_MSG);
      return;
    }
    setLwdBusy(true);
    try {
      const res = await exitApi.saveLwd(exitCase.id, lwdValue);
      toast.success(res.message);
      setExitCase(res.data);
    } catch (e) {
      const msg = e.details?.date_leaving;
      if (msg) setLwdError(Array.isArray(msg) ? msg[0] : msg);
      toast.error(e.message);
    } finally {
      setLwdBusy(false);
    }
  };

  const doCancel = async () => {
    try {
      await exitApi.cancelCase(exitCase.id, cancel);
      toast.success("Exit cancelled.");
      setCancel(null);
      load();
    } catch { /* toast shown */ }
  };

  return (
    <ExitPage
      title="Exit Clearance"
      actions={canCancel && <button className="exitwf-btn ghost" onClick={() => setCancel("")}>Cancel Exit</button>}
    >
      <div className="exitwf-case-head">
        <Link to="/exit-clearance" className="back">‹ All exits</Link>
        <h4>{exitCase.case_no} <span>· {exitCase.employee.name} ({exitCase.employee.code})</span></h4>
        {exitCase.status === "open" ? <Pill kind="open">Pending at {pendingLabels.join(" & ")}</Pill>
          : exitCase.status === "closed" ? <Pill kind="done">Completed</Pill>
          : <Pill>Cancelled{exitCase.cancel_reason ? `: ${exitCase.cancel_reason}` : ""}</Pill>}
      </div>
      {exitCase.status === "open" && !exitCase.can_act_now.length && (
        <div className="exitwf-banner">Nothing is pending with you on this exit. You can view it; the sections open for editing when it's your turn.</div>
      )}

      <Card><Stepper stages={exitCase.stages_detail} caseStatus={exitCase.status} /></Card>

      <Card title="Separation Details">
        <div className="exitwf-grid">
          <ReadField label="Exit Category" value={sep.exit_category} />
          <ReadField label="Exit Type" value={sep.exit_type} />
          <ReadField label="Date of Resignation" value={fmtDate(sep.date_resignation)} />
          <ReadField label="Date Info Received" value={fmtDate(sep.date_info_received)} />
          {canEditLwd ? (
            <Field field={LWD_FIELD} value={lwdValue} error={lwdError} min={dateFromToday(-90)} max={dateFromToday(90)} readOnly={lwdBusy}
              onChange={(_, v) => { setLwdValue(v); setLwdError(undefined); }} />
          ) : (
            <ReadField label="Date of Leaving (LWD)" value={fmtDate(sep.date_leaving)} />
          )}
          <ReadField label="Notice Period After Confirmation" value={sep.notice_after_confirmation != null ? `${sep.notice_after_confirmation} days` : ""} />
          <ReadField label="Notice Period Before Confirmation" value={sep.notice_before_confirmation != null ? `${sep.notice_before_confirmation} days` : ""} />
          <ReadField label="Working Status" value={sep.working_status} />
          <ReadField label="Exit Interview" value={sep.exit_interview} />
          <ReadField label="Exit Remark" value={sep.exit_remark} wide />
        </div>
        {canEditLwd && (
          <div className="exitwf-actions">
            <button className="exitwf-btn" disabled={lwdBusy || lwdValue === sep.date_leaving} onClick={saveLwd}>Save</button>
          </div>
        )}
      </Card>

      {exitCase.stages_detail.filter((s) => s.status !== "skipped").map((s) => (
        <StageSection key={`${s.stage_key}-${s.status}-${exitCase.status}`} exitCase={exitCase} stage={s} onChanged={setExitCase}
          rmOptions={s.stage_key === "EMP" && isHrApprover ? rmOptions : undefined} />
      ))}

      <Card title="Audit Trail">
        <ul className="exitwf-timeline">
          {[...exitCase.audit].reverse().map((a, i) => (
            <li key={i}>
              <span className="ts">{fmtTs(a.at)}</span>
              <b>{a.action}</b>
              {a.stage && <span> · {a.stage}</span>}
              <span className="by"> by {a.by}</span>
            </li>
          ))}
        </ul>
      </Card>

      {cancel !== null && (
        <Modal
          title="Cancel exit"
          onClose={() => setCancel(null)}
          footer={
            <>
              <button className="exitwf-btn ghost" onClick={() => setCancel(null)}>Keep exit</button>
              <button className="exitwf-btn" disabled={cancel.trim().length < 3} onClick={doCancel}>Cancel exit</button>
            </>
          }
        >
          <div className="exitwf-field wide">
            <label htmlFor="exf-cancel">Reason<span className="req">*</span></label>
            <textarea id="exf-cancel" className="form-control" rows={3} value={cancel} onChange={(e) => setCancel(e.target.value)}
              placeholder="e.g. Employee withdrew resignation" />
          </div>
        </Modal>
      )}
    </ExitPage>
  );
}
